import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { CustomerStoryTestimonialProps } from "./index";

const TESTIMONIAL_INTERVAL_MS = 5000;

// Animated testimonial progress bubbles - used on desktop only ATM due to Swiper constraints
const TestimonialProgressBubbles = ({
  testimonials,
  currentTestimonialIndex,
  SetFixedTestimonial,
  isAnimating,
}: {
  testimonials: CustomerStoryTestimonialProps[];
  currentTestimonialIndex: number;
  SetFixedTestimonial: (index: number) => void;
  isAnimating: boolean;
}) => {
  return (
    <>
      {testimonials.map((_, index) => {
        const duration = isAnimating ? TESTIMONIAL_INTERVAL_MS / 1000 : 1;

        return isAnimating ? (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-gray-300 relative rounded-[9px] overflow-hidden"
            initial={{ width: '9px', height: '9px' }}
            animate={{ width: index === currentTestimonialIndex ? ['9px', '35px', '35px', '9px'] : '9px' }}
            transition={{ duration: duration, times: [0, 0.1, 0.9, 1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '9px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['9px', '35px', '9px'] : '9px',
              }}
              transition={{ duration: duration, times: [0, 0.9, 1] }}
              className="w-[9px] h-[9px] absolute z-1  rounded-[9px] bg-white cursor-pointer"
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        ) : (
          <motion.div
            key={`progress-bubble-${index}`}
            className="bg-gray-300 relative rounded-[9px] overflow-hidden"
            initial={{ width: '9px', height: '9px' }}
            animate={{ width: index === currentTestimonialIndex ? ['9px', '35px'] : '9px' }}
            transition={{ duration: duration, times: [0, 0.1] }}
            onClick={() => SetFixedTestimonial(index)}
          >
            <motion.div
              initial={{ width: '9px', left: '0', right: '0' }}
              animate={{
                width: index === currentTestimonialIndex ? ['9px', '35px'] : '9px',
              }}
              transition={{ duration: duration, times: [0, 0.9] }}
              className="w-[9px] h-[9px] absolute z-1  rounded-[9px] bg-white cursor-pointer"
              onClick={() => SetFixedTestimonial(index)}
            />
          </motion.div>
        );
      })}
    </>
  );
};

export const DesktopCustomerStoryTestimonial = ({ testimonials }: { testimonials: CustomerStoryTestimonialProps[] }) => {
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);

  const SetFixedTestimonial = (index: number) => {
    setIsAnimating(false);
    setCurrentTestimonialIndex(index);
  };

  useEffect(() => {
    if (isAnimating) {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
      }, TESTIMONIAL_INTERVAL_MS);

      return () => clearInterval(interval);
    }
  }, [testimonials, currentTestimonialIndex, isAnimating]);

  return (
    <div className="relative h-full">
      <AnimatePresence>
        {/* Progress bubbles */}
        <div className="absolute z-10 top-0 right-0 -translate-x-1/2 flex gap-1 mb-4 bg-gray-200 rounded-20 p-1">
          <TestimonialProgressBubbles
            testimonials={testimonials}
            currentTestimonialIndex={currentTestimonialIndex}
            SetFixedTestimonial={SetFixedTestimonial}
            isAnimating={isAnimating}
          />
        </div>

        {testimonials.map((testimonial, index) => {
          return (
            <motion.div
              key={`customer-story-testimonial-${index}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute  p-4 inset-0 w-full h-full  flex flex-col justify-between"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex max-h-4"
              >
                {testimonial.logo}
              </motion.div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="text-16/24 md:text-h4 font-serif font-500 text-blue-900"
              >
                {testimonial.quote}
              </motion.p>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: index === currentTestimonialIndex ? 1 : 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="flex flex-col"
              >
                <span className="text-14/20 mb-[4px] md:text-p2 font-400 text-blue-1000">{testimonial.author}</span>
                <span className="text-14/20 md:text-micro2 text-blue-1000 uppercase">{testimonial.title}</span>
              </motion.div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};
